import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Loader2, Building2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { isClinicModuleEnabled, readActivatedModules } from '@/lib/clinicModules';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { toast } from 'react-hot-toast';

interface ClinicWithModules {
  id: string;
  name: string;
  is_active: boolean;
  activated_modules: Record<string, boolean>;
}

const AVAILABLE_MODULES = [
  'patients',
  'appointments',
  'journal',
  'invoices',
  'sms',
  'quotes',
  'documents',
  'payments',
  'referrals',
] as const;

export function AdminModules() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const { data: clinics, isLoading } = useQuery({
    queryKey: ['admin-clinic-modules'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinics')
        .select('id, name, is_active, activated_modules')
        .order('name');

      if (error) throw error;
      return (data ?? []).map((c) => ({
        ...c,
        activated_modules: readActivatedModules(c.activated_modules),
      })) as ClinicWithModules[];
    },
  });

  const toggleModule = useMutation({
    mutationFn: async ({
      clinicId,
      moduleKey,
      enabled,
    }: {
      clinicId: string;
      moduleKey: string;
      enabled: boolean;
    }) => {
      const { data, error } = await supabase.rpc('toggle_clinic_module', {
        p_clinic_id: clinicId,
        p_module_key: moduleKey,
        p_enabled: enabled,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-clinic-modules'] });
      toast.success(t('admin.modules.saved'));
    },
    onError: (err) => {
      console.error('toggle_clinic_module failed', err);
      toast.error(t('admin.modules.error'));
    },
  });

  const filteredClinics = clinics?.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <PageLayout title={t('admin.modules.title')} subtitle={t('admin.modules.subtitle')}>

      {/* Search */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder={t('admin.modules.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leadingIcon={<Search className="h-4 w-4" aria-hidden />}
        />
      </div>

      {/* Clinics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClinics?.map((clinic) => (
            <div
              key={clinic.id}
              className="overflow-hidden rounded-lg border border-ds-line bg-ds-card"
            >
              {/* Clinic header */}
              <div className="flex items-center gap-3 border-b border-ds-line bg-ds-sunk px-6 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-teal-tint">
                  <Building2 className="h-5 w-5 text-ds-teal" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-ds-text">{clinic.name}</h3>
                  {!clinic.is_active && (
                    <span className="text-xs text-ds-red">{t('admin.modules.inactive')}</span>
                  )}
                </div>
              </div>

              {/* Module toggles */}
              <div className="grid gap-px bg-ds-line sm:grid-cols-2 lg:grid-cols-4">
                {AVAILABLE_MODULES.map((moduleKey) => {
                  const enabled = isClinicModuleEnabled(clinic.activated_modules, moduleKey);
                  return (
                    <div
                      key={moduleKey}
                      className="flex items-center justify-between bg-ds-card p-4"
                    >
                      <div>
                        <p className="text-sm font-medium text-ds-text">
                          {t(`admin.modules.module_${moduleKey}`)}
                        </p>
                        <p className="text-xs text-ds-muted">
                          {enabled ? t('admin.modules.enabled') : t('admin.modules.disabled')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          toggleModule.mutate({
                            clinicId: clinic.id,
                            moduleKey,
                            enabled: !enabled,
                          })
                        }
                        disabled={toggleModule.isPending}
                        className={
                          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-60 ' +
                          (enabled ? 'bg-ds-teal' : 'bg-ds-toggle-off')
                        }
                        aria-pressed={enabled}
                        aria-label={t('admin.modules.toggle')}
                      >
                        <span
                          aria-hidden="true"
                          className={
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-ds-knob shadow-sm ring-0 transition duration-200 ease-in-out ' +
                            (enabled ? 'translate-x-5' : 'translate-x-0')
                          }
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {filteredClinics?.length === 0 && (
            <div className="rounded-lg border border-ds-line bg-ds-card py-12 text-center">
              <p className="text-sm text-ds-muted">{t('admin.modules.no_clinics')}</p>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
}
